/**
 * Move-Level Replay Recording ユーティリティ
 *
 * ML学習用に各Moveの呼び出しを記録するラッパー。
 * Boardgame.io v0.50 の Immer ベースの状態管理に対応。
 *
 * - INVALID_MOVE 時はログしない
 * - undoToTurnStart はラップしない（状態復元で自然に処理）
 */

import { INVALID_MOVE } from 'boardgame.io/core';
import type { AoSGameState } from '../../types';

// Boardgame.io v0.50 の Move 関数型（context には G, ctx, events, random 等が含まれる）
type MoveFunction = (context: any, ...args: any[]) => any;

/**
 * 個別のMove関数にログ記録を追加するラッパー
 */
export function withMoveLogging(moves: Record<string, MoveFunction>): Record<string, MoveFunction> {
  const wrapped: Record<string, MoveFunction> = {};

  for (const [name, fn] of Object.entries(moves)) {
    // undoToTurnStart はラップしない（undo時にturnStartSnapshotが復元され、当該ターンのログも自然に消える）
    if (name === 'undoToTurnStart') {
      wrapped[name] = fn;
      continue;
    }

    wrapped[name] = (context: any, ...args: any[]) => {
      const result = fn(context, ...args);

      // INVALID_MOVE 時はログしない
      if (result === INVALID_MOVE) return INVALID_MOVE;

      // 成功時にログを記録（G は Immer draft なので直接 push 可能）
      const G: AoSGameState = context.G;
      const ctx = context.ctx;
      G.moveLog.push({
        turn: (G as any).actualTurn || 0,
        phase: ctx.phase || '',
        move: name,
        args: args[0],
      });

      return result;
    };
  }

  return wrapped;
}

/**
 * Phase定義のmovesを一括でラップする
 */
export function wrapPhaseMoves<T extends Record<string, any>>(phases: T): T {
  const wrapped: Record<string, any> = {};

  for (const [name, phase] of Object.entries(phases)) {
    wrapped[name] = {
      ...phase,
      moves: phase.moves ? withMoveLogging(phase.moves) : undefined,
    };
  }

  return wrapped as T;
}
